import React, { useEffect, useState } from "react";
import {
  PersonalizationValue,
  TriggerStepCreate,
  getMembersVariables,
} from "@dots-projects/types";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Checkbox, Spin } from "antd";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import { getGuildRoles } from "../../lib/discordQueries";
import { useQuery } from "react-query";
import { DiscordRoles } from "../../../types/discord_roles";
import { Select } from "antd";
import { getAvailableChannelVariablesChannelsBySteps } from "../../../../../libs/types/src/lib/getAvailableVariables";
import { useGuildChannels } from "./SendStepFormWithCommonParts";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
import TriggerEditorDiscordTextEditor from "./TriggerEditorDiscordTextEditor";
import useRefreshChannelsAndRoles from "./useRefreshChannelsAndRoles";
import RefreshButton from "../RefreshButton/RefreshButton";
import DiscordMemberSingleSelector from "./DiscordSingleMemberSelector";

export type GuildRole = Pick<DiscordRoles, "discord_id" | "name">;

export function useGuildRoles(guildId: string) {
  return useQuery<GuildRole[], Error>(["guildRoles", guildId], async () => {
    const roles: DiscordRoles[] | null = await getGuildRoles(guildId);
    if (
      roles &&
      roles.length > 0 &&
      typeof roles === "object" &&
      Array.isArray(roles)
    ) {
      return roles;
    } else {
      throw new Error(`Failed to fetch guild roles ${guildId}`);
    }
  });
}

/**
 * Renders the form for a "Edit role" step. When the form is submitted, generate the step data and publish an update action to change the role the selected user
 */
export function StepFormEditRoles({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(step.type === "edit_roles");
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const guildRoles = useGuildRoles(state.trigger.current.communityId);
  const [errorSelect, setErrorSelect] = useState(
    ["Select channel", "$channelId"].includes(
      step.configs?.followUpChannelId as string
    )
  );

  const availableChannels = getAvailableChannelVariablesChannelsBySteps(
    state.trigger.current
  );
  const guildChannels = useGuildChannels(state.trigger.current.communityId);

  const additionalValues = {
    userId: step.configs.userIds[0] || "{{user.id}}",
    addRoles: step.configs.addRoles || [], //Deprecated use addRoleIds instead
    removeRoles: step.configs.removeRoles || [], //Deprecated use removeRoleIds instead
    //I did this because react-hook-form doesn't support primitive arrays,
    // they have to be objects, take a look https://github.com/react-hook-form/react-hook-form/discussions/7586
    addRoleIds: step.configs.addRoleIds?.map((roleId) => ({
      id: roleId,
    })),
    removeRoleIds: step.configs.removeRoleIds?.map((roleId) => ({
      id: roleId,
    })),
    ephemeral: step.configs.ephemeral,
    acknowledge: step.configs.acknowledge,
    followUpChannelId: step.configs.followUpChannelId,
    followUpMessage: step.configs.followUpMessage,
    followUpMessageHtml: step.configs.$$editorHtmlFollowUpMessage,
    name: step.name,
  };

  const form = useForm({
    defaultValues: additionalValues,
    mode: "onChange",
  });

  const { handleSubmit, control, watch, setValue } = form;
  const readOnly = !state.trigger.current.isDraft;

  const acknowledge = watch("acknowledge");

  const { replace: replaceAddRoleIdField } = useFieldArray({
    control,
    name: "addRoleIds", // unique name for your Field Array
  });

  const { replace: replaceRemoveRoleIdField } = useFieldArray({
    control,
    name: "removeRoleIds", // unique name for your Field Array
  });

  //Transform all roles names to ids, this is to support the old format
  useEffect(() => {
    if (guildRoles.data) {
      const formValues = form.getValues();
      const addRolesId = guildRoles.data
        .filter((role) => formValues.addRoles.includes(role.name))
        .map((role) => ({
          id: role.discord_id,
        }));
      const removeRolesIds = guildRoles.data
        .filter((role) => formValues.removeRoles.includes(role.name))
        .map((role) => ({
          id: role.discord_id,
        }));

      replaceAddRoleIdField([...(formValues.addRoleIds || []), ...addRolesId]);
      replaceRemoveRoleIdField([
        ...(formValues.removeRoleIds || []),
        ...removeRolesIds,
      ]);
      setValue("addRoles", []);
      setValue("removeRoles", []);
    }
  }, [guildRoles.data]);

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.

    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      //TODO just the current user in flow for now
      stepDraft.configs.userIds = [data.userId];

      //Add and remove roles are deprecated but still supported
      stepDraft.configs.addRoles = data.addRoles;
      stepDraft.configs.removeRoles = data.removeRoles;

      stepDraft.configs.addRoleIds = data.addRoleIds.map(
        (role: { id: string }) => role.id
      );

      stepDraft.configs.removeRoleIds = data.removeRoleIds.map(
        (role: { id: string }) => role.id
      );

      stepDraft.configs.ephemeral = data.ephemeral;
      stepDraft.configs.acknowledge = data.acknowledge;
      // if acknowledge is set, accept followup params
      if (stepDraft.configs.acknowledge) {
        stepDraft.configs.followUpChannelId = data.followUpChannelId;
        stepDraft.configs.followUpMessage = data.followUpMessage;
        stepDraft.configs.$$editorHtmlFollowUpMessage =
          data.followUpMessageHtml;
      } else {
        // if acknowledge is not set, clear followup params
        stepDraft.configs.followUpChannelId = undefined;
        stepDraft.configs.followUpMessage = undefined;
        stepDraft.configs.$$editorHtmlFollowUpMessage = undefined;
      }
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  const children: React.ReactNode[] = [];
  if (guildRoles.data) {
    for (let i = 0; i < guildRoles.data.length; i++) {
      children.push(
        <Select.Option
          key={`${guildRoles.data[i].discord_id}`}
          value={guildRoles.data[i].discord_id}
          name={guildRoles.data[i].name}
        >
          {guildRoles.data[i].name}
        </Select.Option>
      );
    }
  }

  const addRoleChange = (addRoles: string[]) => {
    replaceAddRoleIdField(addRoles.map((roleId) => ({ id: roleId })));
  };

  const removeRoleChange = (removeRoles: string[]) => {
    replaceRemoveRoleIdField(removeRoles.map((roleId) => ({ id: roleId })));
  };

  const { isRefreshLoading, refreshSuccess, refresh, resetRefreshState } =
    useRefreshChannelsAndRoles();

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <FormLabel label="Which user to apply">
        <DiscordMemberSingleSelector
          form={form}
          members={getMembersVariables(insertVariables)}
          defaultMemberId={additionalValues.userId}
          readOnly={readOnly}
          fieldName="userId"
        />
      </FormLabel>

      <FormLabel label="Roles to add">
        <Controller
          control={control}
          name="addRoleIds"
          render={({ field }) => {
            if (guildRoles.isLoading) {
              return <Spin />;
            } else {
              return (
                <Select
                  defaultValue={
                    field.value ? field.value.map((role) => role.id) : []
                  }
                  mode="multiple"
                  style={{ width: "100%" }}
                  allowClear
                  onChange={addRoleChange}
                  key={field.value?.toString()}
                  optionFilterProp="name"
                  dropdownRender={(menu) => (
                    <div>
                      <div className="flex items-center justify-end px-1 py-1">
                        <RefreshButton
                          refresh={refresh}
                          resetRefreshState={resetRefreshState}
                          isLoading={isRefreshLoading}
                          success={refreshSuccess}
                          hintText="Can't find a role? click here to refresh"
                        />
                      </div>
                      {menu}
                    </div>
                  )}
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>

      <FormLabel label="Roles to remove">
        <Controller
          control={control}
          name="removeRoleIds"
          render={({ field }) => {
            if (guildRoles.isLoading) {
              return <Spin />;
            } else {
              return (
                <Select
                  defaultValue={
                    field.value ? field.value.map((role) => role.id) : []
                  }
                  mode="multiple"
                  style={{ width: "100%" }}
                  allowClear
                  onChange={removeRoleChange}
                  key={field.value?.toString()}
                  optionFilterProp="name"
                  dropdownRender={(menu) => (
                    <div>
                      <div className="flex items-center justify-end px-1 py-1">
                        <RefreshButton
                          refresh={refresh}
                          resetRefreshState={resetRefreshState}
                          isLoading={isRefreshLoading}
                          success={refreshSuccess}
                          hintText="Can't find a role? click here to refresh"
                        />
                      </div>
                      {menu}
                    </div>
                  )}
                >
                  {children}
                </Select>
              );
            }
          }}
        />
      </FormLabel>

      <Controller
        control={control}
        name="acknowledge"
        rules={{
          validate: () => {
            let channelId = form.getValues("followUpChannelId");
            if (!acknowledge) {
              return true;
            }
            return (
              channelId !== undefined &&
              channelId !== "Select channel" &&
              channelId !== "$channelId"
            );
          },
        }}
        render={({ field }) => (
          <Checkbox
            checked={!field.value}
            onChange={(e) => {
              field.onChange(!e.target.checked);
            }}
          >
            <span className="text-gray-500">Don't send acknowledgement</span>
          </Checkbox>
        )}
      />

      {acknowledge && (
        <>
          <Controller
            control={control}
            name="followUpChannelId"
            rules={{
              validate: (channelId) => {
                return (
                  channelId !== undefined &&
                  channelId !== "Select channel" &&
                  channelId !== "$channelId"
                );
              },
            }}
            render={({ field }) => (
              <TriggerEditorChannelSelector
                readOnly={readOnly}
                field={field}
                errorSelect={errorSelect}
                trigger={state.trigger.current}
                step={step}
                showRefreshButton
              />
            )}
          />

          <FormLabel label="Message" />
          <TriggerEditorDiscordTextEditor
            form={form}
            htmlField="followUpMessageHtml"
            markdownField="followUpMessage"
            insertVariables={insertVariables}
            defaultValue={step.configs.followUpMessage}
            readOnly={readOnly}
          />
        </>
      )}
    </StepFormWithCommonParts>
  );
}
